import { useEffect, useRef, useState } from 'react';
import type { Customer } from '../components/CustomerPopover';

/**
 * Debounced customer search against /api/customers/search.
 *
 * Empty query returns "recents" (last visited customers in the branch).
 * Any query searches by name / phone / member number.
 *
 * The Customer interface in CustomerPopover already mirrors the API
 * shape; the only field not on the client interface is memberNumber
 * (added in Phase 3.1) — we cast through to avoid a wider refactor.
 */

interface ApiCustomer {
  id: string;
  name: string;
  phone: string;
  email?: string | null;
  memberNumber: string;
  loyaltyPoints: number;
  totalSpentCents: number;
  lastVisitAt: string | null;
}

function fmtLastVisit(at: string | null): string | undefined {
  if (!at) return undefined;
  const d = new Date(at);
  const diff = Date.now() - d.getTime();
  const day = 24 * 60 * 60 * 1000;
  if (diff < day) return 'today';
  if (diff < 2 * day) return 'yesterday';
  if (diff < 7 * day) return `${Math.floor(diff / day)} days ago`;
  if (diff < 30 * day) return `${Math.floor(diff / (7 * day))} weeks ago`;
  return d.toLocaleDateString('en-ZA');
}

function adapt(c: ApiCustomer): Customer {
  return {
    id: c.id,
    name: c.name,
    phone: c.phone,
    email: c.email ?? undefined,
    loyaltyPoints: c.loyaltyPoints,
    lastVisit: fmtLastVisit(c.lastVisitAt),
    totalSpentCents: c.totalSpentCents,
  };
}

export function useCustomerSearch(query: string) {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const my = ++seq.current;
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const url = `/api/customers/search?q=${encodeURIComponent(query)}`;
        const r = await fetch(url, { credentials: 'include' });
        if (!r.ok || my !== seq.current) return;
        const data = (await r.json()) as { customers: ApiCustomer[] };
        setCustomers((data.customers ?? []).map(adapt));
      } finally {
        if (my === seq.current) setLoading(false);
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [query]);

  return { customers, loading };
}

export async function createCustomer(input: {
  name: string;
  phone: string;
  email?: string;
  /** ISO date string YYYY-MM-DD — for the birthday-freebie loyalty perk. */
  dob?: string;
}): Promise<Customer> {
  const r = await fetch('/api/customers', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: input.name,
      phone: input.phone,
      email: input.email || null,
      dob: input.dob || null,
    }),
  });
  if (!r.ok) {
    const data = await r.json().catch(() => ({}));
    throw new Error(data.error ?? `HTTP ${r.status}`);
  }
  const data = (await r.json()) as { customer: ApiCustomer };
  return adapt(data.customer);
}
